<script lang="ts" setup>
  import { ref, watch, computed, onMounted } from 'vue'
  import AppImage from '@/components/AppImage.vue'
  import AppLoading from '@/components/AppLoading.vue'
  import ToastService from '@/components/AppToast/toast'
  import type { RelatedWorkItem } from '@/types/relatedWork'
  import { mapCandidateToRelatedWork, resolveAssociateLimit } from '@/types/relatedWork'
  import httpApi from '@/http/api'
  import { RELATED_WORK_ASSETS, PAGE_SIZE } from './constants'

  const props = defineProps<{
    selectedWork: RelatedWorkItem | null
  }>()

  const emit = defineEmits<{
    close: []
    confirm: [work: RelatedWorkItem | null]
    headerTipPress: []
  }>()

  const draftSelectedId = ref<string | null>(props.selectedWork?.uniqueContentId ?? null)
  const scrollRef = ref<HTMLDivElement | null>(null)
  const relatedLimit = ref(resolveAssociateLimit())
  const items = ref<RelatedWorkItem[]>([])
  const loading = ref(false)
  const loadingMore = ref(false)
  const noMore = ref(false)
  let page = 1

  watch(
    () => props.selectedWork?.uniqueContentId,
    id => {
      draftSelectedId.value = id ?? null
    }
  )

  const selectedCount = computed(() => (draftSelectedId.value ? 1 : 0))

  const fetchList = async (isLoadMore = false) => {
    if (isLoadMore) {
      if (loadingMore.value || noMore.value) return
      loadingMore.value = true
    } else {
      loading.value = true
      page = 1
      noMore.value = false
    }

    try {
      const res = await httpApi.getRelatedWorkCandidates({ page, pageSize: PAGE_SIZE })
      const data = res.data
      if (!isLoadMore && data?.associateLimit != null) {
        relatedLimit.value = resolveAssociateLimit(data.associateLimit)
      }
      const list = (data?.dataList ?? []).map(mapCandidateToRelatedWork)
      if (isLoadMore) {
        items.value = [...items.value, ...list]
      } else {
        items.value = list
      }
      const hasMore = data?.hasMore ?? list.length >= PAGE_SIZE
      noMore.value = !hasMore
      if (hasMore) {
        page += 1
      }
    } catch {
      if (!isLoadMore) {
        items.value = []
      }
    } finally {
      loading.value = false
      loadingMore.value = false
    }
  }

  onMounted(() => {
    void fetchList(false)
  })

  const handleItemPress = (item: RelatedWorkItem) => {
    const isAtLimit = !item.selectable
    const isGrayedBySingleSelect =
      draftSelectedId.value != null && item.uniqueContentId !== draftSelectedId.value

    if (isAtLimit) {
      ToastService({ message: `原作品已到${relatedLimit.value}个关联上限`, type: 'text' })
      return
    }
    if (isGrayedBySingleSelect) {
      ToastService({ message: '最多关联1个作品', type: 'text' })
      return
    }

    draftSelectedId.value =
      draftSelectedId.value === item.uniqueContentId ? null : item.uniqueContentId
  }

  const handleConfirm = () => {
    const work = draftSelectedId.value
      ? (items.value.find(item => item.uniqueContentId === draftSelectedId.value) ?? null)
      : null
    emit('confirm', work)
  }

  const handleClose = () => {
    draftSelectedId.value = props.selectedWork?.uniqueContentId ?? null
    emit('close')
  }

  const handleScroll = () => {
    if (loadingMore.value || noMore.value || items.value.length === 0) return
    const el = scrollRef.value
    if (!el) return
    const isNearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 80
    if (isNearBottom) {
      void fetchList(true)
    }
  }

  const getItemState = (item: RelatedWorkItem) => {
    const isAtLimit = !item.selectable
    const isSelected = draftSelectedId.value === item.uniqueContentId
    const isGrayedBySingleSelect =
      draftSelectedId.value != null && item.uniqueContentId !== draftSelectedId.value
    const isVisuallyDisabled = isAtLimit || isGrayedBySingleSelect
    return { isAtLimit, isSelected, isVisuallyDisabled }
  }
</script>

<template>
  <div class="related-work-select-panel">
    <div class="related-work-panel-header">
      <a class="related-work-panel-back hitems-arrow-left" @click="handleClose" />
      <span class="related-work-panel-title">关联作品</span>
      <button type="button" class="related-work-panel-help" @click.stop="emit('headerTipPress')">
        <img :src="RELATED_WORK_ASSETS.popupTipIcon" alt="规则说明" />
      </button>
    </div>

    <div v-if="loading" class="related-work-panel-loading">
      <AppLoading />
    </div>
    <div v-else-if="items.length === 0" class="related-work-panel-empty">
      暂无可关联的作品，快去创作吧～
    </div>
    <div v-else ref="scrollRef" class="related-work-panel-board" @scroll="handleScroll">
      <div class="related-work-panel-grid">
        <div
          v-for="item in items"
          :key="item.uniqueContentId"
          class="related-work-grid-item"
          :class="{
            'is-selected': getItemState(item).isSelected,
            'is-disabled': getItemState(item).isVisuallyDisabled && !getItemState(item).isSelected
          }"
          @click="handleItemPress(item)"
        >
          <AppImage :src="item.coverUrl" class="related-work-grid-cover" />
          <div
            v-if="getItemState(item).isVisuallyDisabled && !getItemState(item).isSelected"
            class="related-work-grid-overlay"
          />
          <img
            v-if="item.isVideo"
            :src="RELATED_WORK_ASSETS.gridVideoPlayIcon"
            class="related-work-grid-play"
            alt=""
          />
          <div class="related-work-grid-title">
            <span class="related-work-grid-title-text">{{ item.displayOriginalTitle }}</span>
          </div>
          <div class="related-work-grid-check">
            <img
              :src="
                getItemState(item).isSelected
                  ? RELATED_WORK_ASSETS.gridCheckboxSelected
                  : RELATED_WORK_ASSETS.gridCheckboxUnchecked
              "
              alt=""
            />
          </div>
        </div>
      </div>
      <div v-if="loadingMore" class="related-work-panel-footer-loading">
        <AppLoading />
      </div>
      <div v-else-if="noMore && items.length > 0" class="related-work-panel-no-more">
        — 没有更多作品了 —
      </div>
    </div>

    <div class="related-work-panel-footer">
      <span class="related-work-panel-count">最多关联 ({{ selectedCount }}/1) 个作品</span>
      <a class="related-work-panel-confirm font-bold" @click="handleConfirm">确定</a>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
  @import './relatedWork.styl'
</style>
